import { TypedContainerModule } from '@thinkalpha/common/ioc/TypedContainer.js';
import type { ApiBindings } from '@thinkalpha/ui-api/bindings';
import { createInstance, createLegacyInstance, createPublicInstance } from 'src/api';
import { appConfig } from 'src/lib/config';

/**
 * Binds the `@thinkalpha/ui-api` instance contract using the axios factories in
 * `src/api`. The factories and their interceptor chain intentionally stay in
 * `src/api`; this module only decides which factory/service-URL pair each
 * binding name resolves to. See `ApiBindings` for the naming grammar.
 *
 * `src/api` resolves the Logger from the static container at module scope, so
 * this file must not be imported before the host container exists. Hosts load
 * it through the `src/bootstrap/iocModules/loadApiModule` side-effect import,
 * sequenced after their `init/2-container*` import.
 */

function requiredLocatesUrl(): string {
    const url = appConfig.locatesApi;
    if (!url) {
        throw new Error('appConfig.locatesApi is not configured; the Locates API bindings are unavailable');
    }
    return url;
}

export const apiModule = new TypedContainerModule<ApiBindings>((bind) => {
    // ── Platform (config.api) ──
    bind('PlatformApi')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.api }, { useLegacySerialization: false }))
        .inSingletonScope();
    bind('PlatformApiSerializer')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.api }))
        .inSingletonScope();
    bind('PlatformApiSerializerLegacy')
        .toDynamicValue(() => createLegacyInstance({ baseURL: appConfig.api }))
        .inSingletonScope();

    // ── Trading (config.tradingApi) ──
    bind('TradingApi')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.tradingApi }, { useLegacySerialization: false }))
        .inSingletonScope();
    bind('TradingApiSerializer')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.tradingApi }))
        .inSingletonScope();
    bind('TradingApiSerializerLegacy')
        .toDynamicValue(() => createLegacyInstance({ baseURL: appConfig.tradingApi }))
        .inSingletonScope();

    // ── Locates (config.locatesApi) ──
    bind('LocatesApi')
        .toDynamicValue(() => createInstance({ baseURL: requiredLocatesUrl() }, { useLegacySerialization: false }))
        .inSingletonScope();
    bind('LocatesApiSerializer')
        .toDynamicValue(() => createInstance({ baseURL: requiredLocatesUrl() }))
        .inSingletonScope();
    bind('LocatesApiSerializerLegacy')
        .toDynamicValue(() => createLegacyInstance({ baseURL: requiredLocatesUrl() }))
        .inSingletonScope();

    // ── Access (config.accessApi) ──
    bind('AccessApi')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.accessApi }, { useLegacySerialization: false }))
        .inSingletonScope();
    bind('AccessApiPublic')
        .toDynamicValue(() => createPublicInstance({ baseURL: appConfig.accessApi }))
        .inSingletonScope();
    bind('AccessApiSerializer')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.accessApi }))
        .inSingletonScope();
    bind('AccessApiSerializerLegacy')
        .toDynamicValue(() => createLegacyInstance({ baseURL: appConfig.accessApi }))
        .inSingletonScope();

    // ── Quant (config.newQuantApi) ──
    bind('QuantApi')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.newQuantApi }, { useLegacySerialization: false }))
        .inSingletonScope();
    bind('QuantApiSerializer')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.newQuantApi }))
        .inSingletonScope();
    bind('QuantApiSerializerLegacy')
        .toDynamicValue(() => createLegacyInstance({ baseURL: appConfig.newQuantApi }))
        .inSingletonScope();

    // ── NLP (config.nlpApi) ──
    bind('NlpApi')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.nlpApi }, { useLegacySerialization: false }))
        .inSingletonScope();
    bind('NlpApiSerializer')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.nlpApi }))
        .inSingletonScope();
    bind('NlpApiSerializerLegacy')
        .toDynamicValue(() => createLegacyInstance({ baseURL: appConfig.nlpApi }))
        .inSingletonScope();

    // ── Kiasi (config.kiasiApi) ──
    bind('KiasiApi')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.kiasiApi }, { useLegacySerialization: false }))
        .inSingletonScope();

    // ── Table proxy (config.tableProxy) ──
    bind('TableProxyApi')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.tableProxy }, { useLegacySerialization: false }))
        .inSingletonScope();
    bind('TableProxyApiSerializer')
        .toDynamicValue(() => createInstance({ baseURL: appConfig.tableProxy }))
        .inSingletonScope();
    bind('TableProxyApiSerializerLegacy')
        .toDynamicValue(() => createLegacyInstance({ baseURL: appConfig.tableProxy }))
        .inSingletonScope();
});
